<template>
  <div class="edit-blueprint-wrapper">
    <!-- Step-wise flow for Bedrock edit mode -->
    <div v-if="isBedrockEditMode" class="step-container">
      <!-- Document Preview Step -->
      <DocumentPreview 
        v-if="currentStep === 'preview'"
        :blueprintName="displayName"
        :blueprintArn="blueprintArn" 
        :blueprintSchema="blueprintSchema"
        :finalResult="finalResult"
        :docArray="docArray"
        :selectedFile="selectedFile"
        :setBlueprintSchema="setBlueprintSchema"
        :setFinalResult="setFinalResult"
        @complete="handlePreviewComplete"
        @displayNameChange="handleDisplayNameChange"
        :isEditMode="true"
        :selectedTenantUsesBedrock="true"
      />

      <!-- Extraction Fields Step -->
      <BDAExtraction
        v-if="currentStep === 'extraction'"
        :documentTypeName="displayName"
        :sourceDocumentName="selectedFile?.name || 'Document'"
        :tenantId="tenantId"
        :documentTypeId="documentTypeId"
        :blueprintArn="blueprintArn"
        :blueprintSchema="blueprintSchema"
        :isStepMode="true"
        :selectedTenantUsesBedrock="true"
        :isEditMode="true"
        :existingExtractionFields="existingExtractionFields"
        @complete="handleExtractionComplete"
      />

      <!-- Configure Settings Step -->
      <ConfigureVasionSettings
        v-if="currentStep === 'settings'"
        :isStepMode="true"
        :tenantId="tenantId"
        :selectedTenantUsesBedrock="true"
        :collectedStepData="collectedStepData"
        :isEditMode="true"
        :existingVasionSettings="existingVasionSettings"
        :selectedFileName="selectedFile?.name || ''"
        @complete="handleSettingsComplete"
      />
    </div>

    <!-- Original edit blueprint page for non-Bedrock mode -->
    <div v-else class="edit-blueprint-page">
      <div class="page-header">
        <div class="header-content">
          <h1 class="page-title">{{ $t('blueprints.edit_blueprint') || 'Edit Blueprint' }}</h1>
          <p class="page-subtitle">{{ blueprintName }}</p>
        </div>
        <div class="header-actions">
          <button class="back-btn" @click="goBack">
            <svg class="icon" viewBox="0 0 24 24">
              <path fill="currentColor" d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
            </svg>
            {{ $t('common.back') || 'Back' }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>{{ $t('common.loading') || 'Loading blueprint details...' }}</p>
      </div>

      <div v-else-if="error" class="error-container">
        <div class="error-icon">⚠️</div>
        <h3>{{ $t('common.error') || 'Error' }}</h3>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="fetchBlueprintDetails">
          {{ $t('common.retry') || 'Retry' }}
        </button>
      </div>

      <div v-else-if="blueprintData" class="blueprint-content">
        <DocumentPreview 
          :s3DocumentUrl="blueprintData.s3DocumentUrl" 
          :blueprintSchema="blueprintData.blueprintSchema" 
          :selectedFile="blueprintData.selectedFile" 
          :setBlueprintSchema="setBlueprintSchema" 
          :setFinalResult="setFinalResult" 
          :blueprintArn="blueprintData.blueprintArn" 
          :finalResult="blueprintData.finalResult" 
          :docArray="blueprintData.docArray" 
          @complete="handlePreviewComplete" 
          :isEditMode="true" 
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { get, post, put } from '@/utils/api'
import { showToast } from '@/utils/toast'
import DocumentPreview from '@/documentTraning/src/components/DocumentPreview.vue'
import BDAExtraction from '@/components/BDAExtraction/BDAExtraction.vue'
import ConfigureVasionSettings from '@/components/ConfigureVasionSettings/ConfigureVasionSettings.vue'

export default {
  name: 'EditBlueprint',
  components: {
    DocumentPreview,
    BDAExtraction,
    ConfigureVasionSettings,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const loading = ref(true)
    const error = ref(null)
    const blueprintData = ref(null)
    const blueprintName = ref('')
    
    // Bedrock edit mode specific state
    const isBedrockEditMode = ref(false)
    const currentStep = ref('preview')
    const tenantId = ref(null)
    const documentTypeId = ref(null)
    const displayName = ref('')
    const blueprintArn = ref('')
    const blueprintSchema = ref({})
    const finalResult = ref({})
    const docArray = ref([])
    const selectedFile = ref(null)
    const existingExtractionFields = ref([])
    const existingVasionSettings = ref({})
    
    // Collected data from steps
    const collectedStepData = reactive({
      docPreviewData: {},
      extractionMappingList: [],
      vasionFields: {},
    })

    // Get blueprint ID from route params
    const blueprintId = computed(() => route.params.id)

    // Initialize from session storage for Bedrock edit mode
    const initializeBedrockEditMode = () => {
      try {
        const editData = sessionStorage.getItem('blueprintEditData')
        if (editData && route.query.mode === 'edit') {
          const data = JSON.parse(editData)
          console.log('Blueprint Edit Data:', data) // Debug log
          isBedrockEditMode.value = true
          
          // The response structure from backend is directly in data.blueprintDetails
          const blueprintDetails = data.blueprintDetails // Direct access, no .data field
          
          tenantId.value = route.query.tenant_id
          documentTypeId.value = route.query.document_type_id
          displayName.value = decodeURIComponent(route.query.displayName || '')
          blueprintArn.value = route.query.blueprintArn
          
          // Parse blueprint details response from new API structure
          if (blueprintDetails) {
            // Get blueprint from backend response
            const blueprint = blueprintDetails.blueprint
            if (blueprint) {
              // Parse schema if it's a string
              if (typeof blueprint.Schema === 'string') {
                try {
                  blueprintSchema.value = JSON.parse(blueprint.Schema)
                } catch (e) {
                  console.error('Failed to parse blueprint schema:', e)
                  blueprintSchema.value = {}
                }
              } else {
                blueprintSchema.value = blueprint.Schema || {}
              }
            }
            
            // Get result metadata
            finalResult.value = blueprintDetails.resultMetadata || {}
            
            // Create doc array from images if available in resultMetadata
            if (blueprintDetails.resultMetadata?.images) {
              docArray.value = blueprintDetails.resultMetadata.images.map((img, idx) => ({
                id: idx,
                imageUrl: img,
              }))
            }
            
            // Set selected file name from resultMetadata or documentType
            if (blueprintDetails.resultMetadata?.source_file_uri) {
              const fileName = blueprintDetails.resultMetadata.source_file_uri.split('/').pop()
              selectedFile.value = { name: fileName || 'Document' }
            } else {
              selectedFile.value = { name: 'Document' }
            }
            
            // Store existing extraction fields
            if (blueprintDetails.extractionFields) {
              existingExtractionFields.value = blueprintDetails.extractionFields
            }
            
            // Store vasion settings
            if (blueprintDetails.vasionSettings) {
              existingVasionSettings.value = blueprintDetails.vasionSettings
            }
            
            // Use display name from documentType if available
            if (blueprintDetails.documentType?.document_type_display_name) {
              displayName.value = blueprintDetails.documentType.document_type_display_name
            }
          }
          
          // Clear session storage after reading
          sessionStorage.removeItem('blueprintEditData')
          loading.value = false
        } else {
          // If no edit data in session storage, something went wrong
          console.error('No blueprint edit data found in session storage')
          error.value = 'Failed to load blueprint data. Please try again.'
          loading.value = false
        }
      } catch (err) {
        console.error('Error initializing Bedrock edit mode:', err)
        error.value = 'Failed to load blueprint data: ' + err.message
        loading.value = false
      }
    }

    // Fetch blueprint details from API (for non-Bedrock mode)
    const fetchBlueprintDetails = async () => {
      try {
        loading.value = true
        error.value = null

        // Make API call to fetch blueprint details
        const response = await get(`/v1/blueprints/${blueprintId.value}`)

        if (response.data) {
          blueprintData.value = {
            s3DocumentUrl: response.data.s3DocumentUrl || null,
            blueprintSchema: response.data.blueprintSchema || {},
            selectedFile: response.data.selectedFile || {},
            blueprintArn: response.data.blueprintArn || '',
            finalResult: response.data.finalResult || {},
            docArray: response.data.docArray || [],
          }
          blueprintName.value = response.data.BlueprintName || 'Untitled Blueprint'
        } else {
          throw new Error('No blueprint data received')
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || 'Failed to fetch blueprint details'
      } finally {
        loading.value = false
      }
    }

    // Handle blueprint schema updates
    const setBlueprintSchema = (schema) => {
      if (isBedrockEditMode.value) {
        blueprintSchema.value = schema
      } else if (blueprintData.value) {
        blueprintData.value.blueprintSchema = schema
      }
    }

    // Handle final result updates
    const setFinalResult = (result) => {
      if (isBedrockEditMode.value) {
        finalResult.value = result
      } else if (blueprintData.value) {
        blueprintData.value.finalResult = result
      }
    }

    // Handle display name change
    const handleDisplayNameChange = (newName) => {
      displayName.value = newName
    }

    // Handle preview completion (move to extraction step)
    const handlePreviewComplete = () => {
      if (isBedrockEditMode.value) {
        // Store document preview data
        collectedStepData.docPreviewData = {
          blueprintArn: blueprintArn.value,
          blueprintSchema: blueprintSchema.value,
          blueprintName: displayName.value,
          finalResult: finalResult.value,
        }
        currentStep.value = 'extraction'
      } else {
        // Original flow
        // You can add logic here to save the updated blueprint
        // or navigate back to the blueprint list
      }
    }

    // Handle extraction completion (move to settings step)
    const handleExtractionComplete = (data) => {
      if (data?.extractionMappingList) {
        collectedStepData.extractionMappingList = data.extractionMappingList
      }
      currentStep.value = 'settings'
    }

    // Handle settings completion (update the document type)
    const handleSettingsComplete = async () => {
      try {
        // For edit mode, we need to update existing entries
        const updateData = {
          docPreviewData: collectedStepData.docPreviewData,
          ExtractionMappingList: collectedStepData.extractionMappingList,
          vasionFields: collectedStepData.vasionFields,
          tenantId: tenantId.value,
          documentTypeId: documentTypeId.value,
          userId: JSON.parse(localStorage.getItem('UserData'))?.id || 1,
          isUpdate: true, // Flag to indicate this is an update
        }
        
        // Call update API
        const response = await put('/v1/bedrock/update-training-data', updateData)
        
        if (response?.data?.code === 200) {
          showToast({
            title: 'Success',
            message: 'Document type updated successfully',
            type: 'success',
          })
          
          // Navigate back to document types page
          const urlName = localStorage.getItem('url_name')
          router.push(`/${urlName}/configure/document-types`)
        } else {
          throw new Error(response?.data?.message || 'Failed to update document type')
        }
      } catch (error) {
        showToast({
          title: 'Error',
          message: error.message || 'Failed to update document type',
          type: 'error',
        })
      }
    }

    // Navigate back
    const goBack = () => {
      router.go(-1)
    }

    // Fetch blueprint details when component mounts
    onMounted(() => {
      // Check for Bedrock edit mode
      if (route.query.mode === 'edit') {
        initializeBedrockEditMode()
      } else if (blueprintId.value) {
        fetchBlueprintDetails()
      } else {
        error.value = 'No blueprint ID provided'
        loading.value = false
      }
    })

    return {
      loading,
      error,
      blueprintData,
      blueprintName,
      isBedrockEditMode,
      currentStep,
      tenantId,
      documentTypeId,
      displayName,
      blueprintArn,
      blueprintSchema,
      finalResult,
      docArray,
      selectedFile,
      existingExtractionFields,
      existingVasionSettings,
      collectedStepData,
      fetchBlueprintDetails,
      setBlueprintSchema,
      setFinalResult,
      handleDisplayNameChange,
      handlePreviewComplete,
      handleExtractionComplete,
      handleSettingsComplete,
      goBack,
    }
  },
}
</script>

<style scoped>
.edit-blueprint-page {
  padding: 24px;
  max-width: 100%;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-content {
  flex: 1;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.page-subtitle {
  margin: 0;
  font-size: 16px;
  color: #666;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #e0e0e0;
  border-color: #ccc;
}

.back-btn .icon {
  width: 20px;
  height: 20px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  color: #666;
  font-size: 16px;
  margin: 0;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-container h3 {
  margin: 0 0 12px 0;
  color: #d32f2f;
  font-size: 20px;
}

.error-container p {
  color: #666;
  font-size: 16px;
  margin: 0 0 20px 0;
  max-width: 400px;
}

.retry-btn {
  padding: 12px 24px;
  background: #1976d2;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.retry-btn:hover {
  background: #1565c0;
}

.blueprint-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Responsive design */
@media (max-width: 768px) {
  .edit-blueprint-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
